import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api/axios";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import { User, Phone, Mail, MapPin, Globe, X, Edit2, Loader2, Camera } from "lucide-react";

const compressImage = (dataUrl, maxWidth = 400, maxHeight = 400, quality = 0.7) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

const ProfileModal = ({ isOpen, onClose, locketId, token, showToast }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "", preferredLanguage: "both", photo: "" });
  const photoInputRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.getCustomerProfile(locketId, token);
      } else {
        res = await api.get(`/qr/${locketId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const p = res.data.profile || {};
      setProfile(p);
      setFormData({
        name: p.name || "",
        phone: p.phone || "",
        email: p.email || "",
        address: p.address || "",
        preferredLanguage: p.preferredLanguage || "both",
        photo: p.photo || "",
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [locketId, token]);

  useEffect(() => {
    if (isOpen && token) {
      fetchProfile();
      setIsEditing(false);
    }
  }, [isOpen, token, fetchProfile]);

  const startCamera = async () => {
    setCameraError(null);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      setCameraError(
        err.name === "NotAllowedError"
          ? "Camera permission denied. Allow camera in browser settings or upload a photo instead."
          : "Camera not available. Use 'Upload photo' or check that the camera is connected."
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setShowCamera(false);
    setCameraError(null);
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const raw = canvas.toDataURL("image/jpeg", 0.85);
    const compressed = await compressImage(raw);
    setFormData((d) => ({ ...d, photo: compressed }));
    stopCamera();
  };

  // Stop camera stream when modal unmounts — empty deps so it only runs on unmount, not on every showCamera toggle
  // (showCamera → false already calls stopCamera() explicitly, which cleans up streamRef)
  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (shouldUseMockData()) {
        await mockApiService.updateCustomerProfile(locketId, token, formData);
      } else {
        await api.put(`/qr/${locketId}/profile`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setProfile((prev) => ({ ...prev, ...formData }));
      setIsEditing(false);
      showToast?.("Profile updated");
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details?.[0]?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const langLabels = { english: "English", hindi: "Hindi", both: "English & Hindi" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-md max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">Account Profile</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="py-6 text-center text-red-600">{error}</div>
          ) : profile && !isEditing ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {profile.photo ? (
                  <img src={profile.photo} alt="You" className="w-14 h-14 rounded-lg object-cover border-2 border-gray-200 shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center shrink-0">
                    <Camera size={24} className="text-gray-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Your photo</p>
                  <p className="text-sm font-medium text-gray-700">Shown on public emergency page for validation</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User size={20} className="text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Name</p>
                  <p className="font-semibold text-gray-900">{profile.name || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone size={20} className="text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                  <p className="font-semibold text-gray-900">{profile.phone || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail size={20} className="text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                  <p className="font-semibold text-gray-900">{profile.email || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin size={20} className="text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Address</p>
                  <p className="font-semibold text-gray-900">{profile.address || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Globe size={20} className="text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Preferred Language</p>
                  <p className="font-semibold text-gray-900">{langLabels[profile.preferredLanguage] || profile.preferredLanguage || "—"}</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-150"
              >
                <Edit2 size={18} />
                Edit Profile
              </button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your photo</label>
                <p className="text-xs text-gray-500 mb-2">Shown on the public emergency page so others can verify they are helping the right person.</p>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file || !file.type.startsWith("image/")) return;
                    const reader = new FileReader();
                    reader.onload = async () => {
                      const compressed = await compressImage(reader.result);
                      setFormData((d) => ({ ...d, photo: compressed }));
                    };
                    reader.readAsDataURL(file);
                    e.target.value = "";
                  }}
                  className="hidden"
                />
                <div className="flex items-center gap-4 flex-wrap">
                  {formData.photo ? (
                    <>
                      <img src={formData.photo} alt="Preview" className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200" />
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={startCamera} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                          Take new photo
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => photoInputRef.current?.click()}
                          className="text-sm font-semibold text-gray-600 hover:text-gray-800"
                        >
                          Upload different photo
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2 w-full">
                      <button
                        type="button"
                        onClick={startCamera}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 transition-colors duration-150 font-semibold"
                      >
                        <Camera size={20} />
                        Open camera
                      </button>
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors duration-150 font-semibold"
                      >
                        Upload photo
                      </button>
                    </div>
                  )}
                </div>
                {cameraError && !showCamera && <p className="text-sm text-amber-700 mt-2 font-medium">{cameraError}</p>}
              </div>

              {showCamera && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70" onClick={stopCamera}>
                  <div className="bg-white rounded-lg shadow-md max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                      <span className="font-bold text-gray-800">Take your photo</span>
                      <button type="button" onClick={stopCamera} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
                        Cancel
                      </button>
                    </div>
                    <div className="p-4">
                      <div className="relative rounded-lg overflow-hidden bg-gray-900 aspect-[4/3]">
                        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button type="button" onClick={captureFromCamera} className="flex-1 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold">
                          Take photo
                        </button>
                        <button type="button" onClick={stopCamera} className="px-4 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData((d) => ({ ...d, phone: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10-digit Indian number"
                  required
                  pattern="[6-9]\d{9}"
                  title="Valid Indian phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((d) => ({ ...d, email: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData((d) => ({ ...d, address: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Language</label>
                <select
                  value={formData.preferredLanguage}
                  onChange={(e) => setFormData((d) => ({ ...d, preferredLanguage: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="both">English & Hindi</option>
                </select>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setIsEditing(false); setError(null); }}
                  className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
